import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Send, Phone, Loader2, MessageSquare } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuickSmsModal({ open, onClose, certData }) {
  const [phone, setPhone] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (!open || !certData) return null;

  const clientName = certData.client_name || certData.client_company || certData.homeowner_name || "";
  const address = certData.installation_address || certData.client_address || "";
  const certRef = certData.certificate_id || "";

  // Use system_4 = "Certificate Ready" template
  const templateId = "system_4";
  const variables = { name: clientName, address };

  const preview = `Hi ${clientName || "{name}"}, your electrical certificate for ${address || "{address}"} is now ready to view. You can access it via your customer portal link.`;

  const handleSend = async () => {
    if (!phone.trim()) { toast.error("Please enter a phone number"); return; }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/sms/send-template`, {
        template_id: templateId, to: phone.trim(), variables,
      }, { withCredentials: true });
      toast.success(data.mocked ? "SMS queued (test mode)" : "SMS sent to customer!");
      onClose();
      setPhone("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send SMS");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid="quick-sms-modal">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-md mx-4 rounded-t-2xl sm:rounded-2xl overflow-hidden animate-fade-in-up" style={{ backgroundColor: 'var(--bg-card)' }}
        onTouchMove={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--border-light)' }}>
          <div className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-[#D97706]" />
            <div>
              <h3 className="text-base font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>Send SMS to Customer</h3>
              <p className="text-[10px]" style={{ color: 'var(--text-muted)', fontFamily: 'IBM Plex Mono, monospace' }}>Ref: {certRef}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100" data-testid="close-quick-sms"><X className="w-5 h-5" style={{ color: 'var(--text-muted)' }} /></button>
        </div>

        <div className="px-5 py-4 space-y-4 overflow-y-auto" style={{ maxHeight: '60vh', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>
          {clientName && (
            <div className="flex items-center gap-2 rounded-xl p-2.5" style={{ backgroundColor: 'var(--row-alt)' }}>
              <span className="text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>To:</span>
              <span className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{clientName}</span>
            </div>
          )}

          <div>
            <label className="text-xs font-semibold block mb-1.5" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-secondary)' }}>Phone Number</label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-muted)' }} />
              <input
                data-testid="quick-sms-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="07450 123 456"
                className="w-full rounded-xl border px-3 pl-10 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#D97706]"
                style={{ backgroundColor: 'var(--bg-input)', borderColor: 'var(--border-main)', color: 'var(--text-primary)' }}
                autoFocus
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold block mb-1.5" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-secondary)' }}>Message Preview</label>
            <div className="rounded-xl p-3 text-sm leading-relaxed" style={{ backgroundColor: 'var(--row-alt)', color: 'var(--text-primary)' }} data-testid="quick-sms-preview">
              {preview}
            </div>
          </div>
        </div>

        <div className="px-5 py-4 border-t flex items-center justify-between" style={{ borderColor: 'var(--border-light)' }}>
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>1 SMS credit will be used</span>
          <button onClick={handleSend} disabled={sending} data-testid="quick-sms-send"
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-[#D97706] hover:bg-[#B45309] transition-colors disabled:opacity-50">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {sending ? "Sending..." : "Send SMS"}
          </button>
        </div>
      </div>
    </div>
  );
}
